import { z } from 'zod';
import {
  Gender,
  TOURNAMENT_AGE_MAX_UNRESTRICTED,
  TOURNAMENT_AGE_MIN_UNRESTRICTED,
} from '@/types/tournament.model';

/**
 * Validation schema for the Padel Team Config step.
 * Padel-specific: playersPerTeam is always 2.
 */
export const padelTeamConfigStepSchema = z
  .object({
    gender: z.nativeEnum(Gender, {
      errorMap: () => ({ message: 'Selecciona un género válido' }),
    }),
    playersPerTeam: z
      .number()
      .min(2, 'El pádel requiere 2 jugadores por equipo')
      .max(2, 'El pádel requiere 2 jugadores por equipo'),
    minCategory: z.object({
      id: z.number(),
      name: z.string(),
      minimumElo: z.number(),
      sport: z.object({
        uid: z.string(),
        name: z.string(),
      }),
    }),
    maxCategory: z.object({
      id: z.number(),
      name: z.string(),
      minimumElo: z.number(),
      sport: z.object({
        uid: z.string(),
        name: z.string(),
      }),
    }),
    forceSum: z.boolean(),
    minAgeRestrictionEnabled: z.boolean().default(false),
    maxAgeRestrictionEnabled: z.boolean().default(false),
    minAge: z.number().int().min(0).optional(),
    maxAge: z.number().int().min(0).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.minAgeRestrictionEnabled) {
      if (data.minAge === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Indica la edad mínima o desactiva la restricción',
          path: ['minAge'],
        });
      }
    }
    if (data.maxAgeRestrictionEnabled) {
      if (data.maxAge === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Indica la edad máxima o desactiva la restricción',
          path: ['maxAge'],
        });
      }
    }

    const effMin = data.minAgeRestrictionEnabled
      ? (data.minAge ?? TOURNAMENT_AGE_MIN_UNRESTRICTED)
      : TOURNAMENT_AGE_MIN_UNRESTRICTED;
    const effMax = data.maxAgeRestrictionEnabled
      ? (data.maxAge ?? TOURNAMENT_AGE_MAX_UNRESTRICTED)
      : TOURNAMENT_AGE_MAX_UNRESTRICTED;

    if (effMin > effMax) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'La edad mínima no puede ser mayor que la edad máxima',
        path: ['minAge'],
      });
    }
  });

export type PadelTeamConfigStepData = z.infer<typeof padelTeamConfigStepSchema>;
